import React, { useEffect, useState } from "react";
import { Box, TextField, FormControl, Typography, MenuItem, Select, InputLabel, Divider, useTheme } from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../components/Header";
import { useAlert } from "../../../global/AlertContext";
import { useNavigate, useParams } from "react-router-dom";
import { ticket_types } from "../../../constants/enums";
import { useGetProductsQuery } from "../../../services/productService";
import { useGetTicketByIdQuery, useUpdateTicketMutation } from "../../../services/ticketService";
import DosagesList from "../../../components/DosagesList";
import ErrorComponent from "../../../components/ErrorComponent";
import LoadingComponent from "../../../components/LoadingComponent";
import { tokens } from "../../../theme";
import SuccessButton from "../../../components/SuccessButton";
import SearchSelect from "../../../components/SearchSelect"; // Import the SearchSelect component
import CancelButton from "../../../components/CancelButton";

const EditTicket = () => {
  const showAlert = useAlert();
  const navigate = useNavigate();
  const { id } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const { data: ticket, isLoading, error, refetch } = useGetTicketByIdQuery(id);
  const [updateTicket] = useUpdateTicketMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const { data: products } = useGetProductsQuery({
    name: searchTerm,
  });

  const [dosages, setDosages] = useState([]);

  useEffect(() => {
    if (ticket) {
      setDosages(ticket.dosages || []);
    }
  }, [ticket]);

  const handleFormSubmit = async (values, { resetForm }) => {
    if (dosages.length > 0) {
      try {
        await updateTicket({ id, updatedTicket: { ...values, dosages } }).unwrap();
        await refetch();
        showAlert("Ticket actualizado!", "success");
        navigate("/tickets");
      } catch (error) {
        showAlert(error.message, "error");
      }
    } else {
      showAlert("Debe agregar al menos una dosis", "error");
    }
  };

  const initialValues = {
    name: ticket?.name || "",
    type: ticket?.type || "",
    product_id: "",
    quantity: 1,
    day: ticket?.day || 1,
  };

  const ticketSchema = yup.object().shape({
    name: yup.string().required("Requerido"),
    type: yup.string().required("Requerido"),
    day: yup.number().required("Requerido"),
  });

  const handleAddDosage = (values, { setFieldValue }) => {
    if (!values.product_id) {
      showAlert("Debe seleccionar un producto", "error");
      return;
    }
    if (values.quantity < 0) {
      showAlert("La cantidad debe ser mayor a 0", "error");
      return;
    }
  
    const isProductAlreadyAdded = dosages.some((dosage) => dosage.product_id === values.product_id);
    if (isProductAlreadyAdded) {
      showAlert("Este producto ya ha sido agregado", "error");
      return;
    }
  
    const product = products?.records?.find((p) => p.id === values.product_id);
    if (product) {
      const newDosage = {
        ticket_id: id,
        product_id: values.product_id,
        quantity: values.quantity,
        name: product.name,
        product_UOM: product.UOM,
      };
      setDosages([...dosages, newDosage]);
  
      // Reset only the product_id and quantity fields
      setFieldValue("product_id", "");
      setFieldValue("quantity", 1);
      setSearchTerm(""); // Clear the search term
    }
  };

  if (isLoading) return <LoadingComponent />;
  if (error) return <ErrorComponent />;

  return (
    <Box m="20px">
      <Header title="Tickets" subtitle="Editar Ticket" />
      <Formik
        onSubmit={(values, { resetForm }) => handleFormSubmit(values, { resetForm })}
        initialValues={initialValues}
        validationSchema={ticketSchema}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue, resetForm }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Nombre del Ticket"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.name}
                name="name"
                error={!!touched.name && !!errors.name}
                helperText={touched.name && errors.name}
                sx={{ gridColumn: "span 1" }}
              />
              <FormControl fullWidth variant="filled" sx={{ gridColumn: "span 1" }}>
                <InputLabel id="type-label">Tipo</InputLabel>
                <Select
                  labelId="type-label"
                  id="type"
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={!!touched.type && !!errors.type}
                >
                  {ticket_types.map((t) => (
                    <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                  ))}
                </Select>
                {touched.type && errors.type && (
                  <Typography variant="caption" color="error">
                    {errors.type}
                  </Typography>
                )}
              </FormControl>
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Día"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.day}
                name="day"
                error={!!touched.day && !!errors.day}
                helperText={touched.day && errors.day}
                sx={{ gridColumn: "span 1" }}
              />
              {/* Product, Quantity, and Add Button in the same row */}
              <Box sx={{ gridColumn: "span 3", display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "30px" }}>
                <SearchSelect
                  label="Producto"
                  options={products?.records || []}
                  value={products?.records?.find((p) => p.id === values.product_id) || null}
                  onChange={(newValue) => {
                    setFieldValue("product_id", newValue?.id || "");
                  }}
                  onInputChange={(newInputValue) => {
                    setSearchTerm(newInputValue); // Update search term
                  }}
                  getOptionLabel={(option) => option.name}
                  error={!!touched.product_id && !!errors.product_id}
                  helperText={touched.product_id && errors.product_id}
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="number"
                  label="Cantidad"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  value={values.quantity}
                  name="quantity"
                  inputProps={{ min: 1 }} // Ensure quantity is at least 1
                />
                <Box textAlign="center">
                  <SuccessButton
                    label="Agregar Dosificación"
                    onClick={(e) => {
                      e.preventDefault(); // Prevent the form from submitting
                      handleAddDosage(values, { setFieldValue });
                    }}
                  />
                </Box>
              </Box>
            </Box>

            {/* Divider */}
            <Divider sx={{ my: "20px" }} />
            <Typography variant="h5" fontWeight="bold" mb="16px" color={colors.grey[100]}>
              Dosificación
            </Typography>
            <Box mt="20px">
              <DosagesList dosagesList={dosages} setDosagesList={setDosages} />
            </Box>
            <Box
              display="flex"
              justifyContent="end"
              textAlign="center"
              gap="4px"
            >
              <CancelButton label="Cancelar" onClick={() => navigate('/tickets')}/>
              <SuccessButton label="Actualizar Ticket" />
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default EditTicket;